/**
 * Combined-dashboard overview data hook.
 *
 * Reuses the exact same service calls / React Query keys OverviewPage.tsx
 * already uses for GSC (search-performance), WP SEO health, technical-audit,
 * and blog-content — no new backend endpoints. Sharing the query keys also
 * means this hook rides the same cache as the SEO Overview tab when both are
 * mounted, instead of double-fetching.
 *
 * See frontend/src/types/dashboardOverview.ts for the returned shape and
 * per-field provenance/caveats.
 */
import { useQuery } from "@tanstack/react-query";
import {
  fetchAggregateMetrics,
  fetchDailyMetrics,
  fetchSyncStatus,
  type Period,
} from "@/services/searchPerformanceService";
import { fetchWPSeoHealth } from "@/services/wpSeoHealthService";
import { technicalAuditService } from "@/services/technicalAuditService";
import { blogCreationService } from "@/services/blogCreationService";
import { apiClient } from "@/services/apiClient";
import type {
  DashboardOverviewData,
  DashboardWorkDoneItem,
} from "@/types/dashboardOverview";

// The combined client dashboard has no picker anymore (Carl's ruling, 2026-08-11:
// default to a rolling window, drop the broken 7d/28d/90d selector). GSC goes
// through the shared search-performance endpoint, whose `Period` union is
// deliberately 7d/28d/90d only -- "30d" is explicitly rejected there (see
// test_daily_route_rejects_invalid_period), so forcing 30d onto that shared
// route would disturb the untouched Overview pages. We therefore request the
// closest SUPPORTED window, "28d", and -- crucially -- caption the Search
// Performance block from the aggregate response's OWN period_start/period_end
// (see `search` below), so the label always matches the window actually
// fetched rather than claiming a 30-day range this endpoint can't cleanly give.
const GSC_PERIOD: Period = "28d";
// The rolling window for the content/work-done "in the last N days" date
// filters, which are plain client-side day-count math (not the GSC endpoint) --
// a genuine 30 days, matching the dashboard's rolling-30 default.
const CONTENT_WINDOW_DAYS = 30;

/** Same endpoint/params WorkDoneFeed.tsx uses for its site-health feed source. */
interface FixHistoryItem {
  occurred_at: string;
  issue_type: string;
  pages_affected: number;
  action: string;
}

async function fetchFixHistory(
  tenantId: string,
  days: number,
): Promise<FixHistoryItem[]> {
  const { data } = await apiClient.get<{ items: FixHistoryItem[] }>(
    "/technical-audit/fix-history",
    { params: { tenant_id: tenantId, days: Math.min(days, 90), limit: 20 } },
  );
  return data.items;
}

export function useDashboardOverviewData(
  tenantId: string | null | undefined,
): DashboardOverviewData {
  const tenantScope = tenantId ?? "no-tenant";
  const periodDays = CONTENT_WINDOW_DAYS;
  const hasTenant = !!tenantId;

  // === GSC (search-performance) — same query keys as OverviewPage ===
  const { data: aggregate, isLoading: aggLoading } = useQuery({
    queryKey: ["search-performance", tenantScope, "aggregate", GSC_PERIOD],
    queryFn: () => fetchAggregateMetrics(tenantId, GSC_PERIOD),
    staleTime: 5 * 60 * 1000,
    enabled: hasTenant,
  });
  const { data: syncStatus, isLoading: syncLoading } = useQuery({
    queryKey: ["search-performance", tenantScope, "sync-status"],
    queryFn: () => fetchSyncStatus(tenantId),
    staleTime: 60 * 1000,
    enabled: hasTenant,
  });
  const { data: gscDaily, isLoading: dailyLoading } = useQuery({
    queryKey: ["search-performance", tenantScope, "daily", GSC_PERIOD],
    queryFn: () => fetchDailyMetrics(tenantId, GSC_PERIOD),
    staleTime: 5 * 60 * 1000,
    enabled: hasTenant,
  });

  // === WP SEO health — same query key as OverviewPage ===
  const { data: wpSeo, isLoading: wpSeoLoading } = useQuery({
    queryKey: ["wp-seo-health", tenantScope],
    queryFn: () => fetchWPSeoHealth(tenantId),
    staleTime: 10 * 60 * 1000,
    enabled: hasTenant,
    retry: false,
  });

  // === Technical audit latest run — same query key as OverviewPage ===
  const { data: taRuns, isLoading: taLoading } = useQuery({
    queryKey: ["technical-audit", tenantScope, "latest-run"],
    queryFn: () => technicalAuditService.listRuns(tenantId ?? "", 1, 5),
    staleTime: 10 * 60 * 1000,
    enabled: hasTenant,
    retry: false,
  });

  // === Content completions — same query key as OverviewPage ===
  const { data: contentList, isLoading: contentLoading } = useQuery({
    queryKey: ["blog-content", tenantScope, "completed-count", CONTENT_WINDOW_DAYS],
    queryFn: () =>
      blogCreationService.list({ status: "completed", page_size: 100 }, undefined, tenantId),
    staleTime: 5 * 60 * 1000,
    enabled: hasTenant,
    retry: false,
  });

  // === Fix history — same endpoint WorkDoneFeed already calls ===
  const { data: fixHistory, isLoading: fixLoading } = useQuery({
    queryKey: ["ta-fix-history-feed", tenantScope],
    queryFn: () => fetchFixHistory(tenantId!, periodDays),
    staleTime: 5 * 60 * 1000,
    enabled: hasTenant,
    retry: false,
  });

  // --- Derive search ---
  const gscConnected = !!aggregate && syncStatus?.status !== "never_synced";
  const seoAgg = wpSeo?.aggregate;
  const pagesIndexed =
    seoAgg && seoAgg.total_pages > 0
      ? seoAgg.total_pages - seoAgg.noindex_count
      : null;

  const search = gscConnected && aggregate
    ? {
        clicks: aggregate.total_clicks,
        clicks_delta_pct: aggregate.clicks_change_pct,
        impressions: aggregate.total_impressions,
        impressions_delta_pct: aggregate.impressions_change_pct,
        avg_position: aggregate.avg_position,
        avg_position_delta: aggregate.position_change,
        pages_indexed: pagesIndexed,
        clicks_trend: gscDaily?.days.map((d) => d.clicks) ?? [],
        // The REAL window these KPIs cover, from the endpoint itself — the
        // Search Performance caption reads this, never a hardcoded range.
        period_start: aggregate.period_start ?? null,
        period_end: aggregate.period_end ?? null,
      }
    : null;

  // --- Derive site health ---
  const taLatest = taRuns?.runs
    ?.filter((r) => r.status === "completed")
    .sort((a, b) => (b.completed_at ?? "").localeCompare(a.completed_at ?? ""))[0];
  const critical = taLatest ? (taLatest.issues_by_severity?.critical ?? 0) : null;
  const warnings = taLatest ? (taLatest.issues_by_severity?.warning ?? 0) : null;
  const crawlDate = taLatest?.completed_at ? taLatest.completed_at.slice(0, 10) : null;
  const pagesHealthyPct =
    seoAgg && seoAgg.total_pages > 0
      ? Math.round((seoAgg.pages_with_good_seo / seoAgg.total_pages) * 100)
      : null;

  // --- Derive content ---
  const windowStart = Date.now() - periodDays * 24 * 60 * 60 * 1000;
  const publishedCount = contentList
    ? contentList.items.filter(
        (item) => item.completed_at && Date.parse(item.completed_at) >= windowStart,
      ).length
    : null;

  // --- Derive work-done feed (content completions + technical-audit fixes) ---
  const workDone: DashboardWorkDoneItem[] = [];
  for (const item of contentList?.items ?? []) {
    if (item.completed_at && Date.parse(item.completed_at) >= windowStart) {
      workDone.push({
        date: item.completed_at,
        text: `Published ${item.title || item.keyword || "article"}`,
      });
    }
  }
  const fixByType = new Map<string, { count: number; latestDate: string }>();
  for (const item of fixHistory ?? []) {
    const existing = fixByType.get(item.issue_type);
    if (existing) {
      existing.count++;
    } else {
      fixByType.set(item.issue_type, { count: 1, latestDate: item.occurred_at });
    }
  }
  for (const [issueType, { count, latestDate }] of fixByType) {
    workDone.push({
      date: latestDate,
      text: `Fixed ${count} ${issueType.replace(/_/g, " ")} issue${count === 1 ? "" : "s"}`,
    });
  }
  workDone.sort((a, b) => Date.parse(b.date) - Date.parse(a.date));

  const isLoading =
    aggLoading || syncLoading || dailyLoading || wpSeoLoading || taLoading || contentLoading || fixLoading;

  return {
    search,
    gscConnected,
    siteHealth: {
      critical,
      warnings,
      pages_healthy_pct: pagesHealthyPct,
      crawl_date: crawlDate,
    },
    content: { published_count: publishedCount },
    workDone: workDone.slice(0, 8),
    isLoading,
  };
}
